import { Match2PowerupType } from 'src/replicant/match2/match2.ruleset';
import {
  randomPick,
  type Entity,
  type QueriesObject,
  type QueryResults,
  type System,
} from '../../odie';
import StateObserver, { user } from 'src/StateObserver';
import { BlockComponent } from '../components/BlockComponent';
import { GlowComponent } from '../components/GlowComponent';
import { PhaseComponent } from '../components/PhaseComponent';
import { BlockId, PowerBlockType, powerBlockTypes } from '../defs/block';
import { PowerBlockEntity } from '../entities/BlockEntity';
import type { GameScene } from '../GameScene';
import {
  blockIsBinded,
  blockIsFrozen,
  blockIterateNeighbors,
} from '../util/blockTools';
import { waitFor } from '../../lib/util/waitFor';
import { isMatch2TimedRewardActive } from 'src/replicant/match2/match2.getters';

/*
    handles power block specifics such as combo glows
*/
export class PowerBlockSystem implements System {
  // constants
  //-------------------------------------------------------------------------
  public static readonly NAME = 'power';
  public static Queries: QueriesObject = {
    block: {
      components: [BlockComponent, GlowComponent],
    },
    phase: {
      components: [PhaseComponent],
      modified: true,
    },
  };

  // fields
  //-------------------------------------------------------------------------
  // injected
  public scene!: GameScene;
  public queries!: QueryResults;
  private _powerupsQueue: Match2PowerupType[] = [];
  private _freePowerupsQueue: Match2PowerupType[] = [];

  // impl
  //-------------------------------------------------------------------------
  public reset() {
    this._powerupsQueue.length = 0;
    this._freePowerupsQueue.length = 0;
  }

  public modifiedQuery(
    entity: Entity,
    component: PhaseComponent,
    properties: { phase: string },
  ) {
    // if input phase update power block combo glows
    if (properties.phase && component.phase === 'input') {
      this._updateComboGlows();
    }
  }

  public refreshComboGlows() {
    this._updateComboGlows();
  }

  public queueExtraPowerups(powerups: Match2PowerupType[], isFree = false) {
    const queue = isFree ? this._freePowerupsQueue : this._powerupsQueue;
    queue.push(...powerups);
  }

  public clearExtraPowerups() {
    this._freePowerupsQueue.length = 0;
    this._powerupsQueue.length = 0;
  }

  public async applyExtraPowerups() {
    const activeBasicBlocksCount =
      this.scene.basicBlockSystem.getActiveBasicBlocks().length;
    const powerupsQueueCount =
      this._powerupsQueue.length + this._freePowerupsQueue.length;

    if (!activeBasicBlocksCount || !powerupsQueueCount) {
      // Unlock gameplay input if there are no powerups to apply
      this.scene.phaseSystem.setInputDisable(false);
      return;
    }

    const now = StateObserver.now();
    const baseDelay = 500;
    const delayPerPowerup = 350;

    // Disable input while powerups are being applied
    this.scene.phaseSystem.setInputDisable(true);
    this.scene.isApplyingExtraPowerups = true;

    for (let index = 0; index < activeBasicBlocksCount; index++) {
      let powerup = this._powerupsQueue.shift();

      // If there is a selected powerup, consume it
      if (powerup) {
        this.consumePowerUp(powerup, now);
      }
      // Otherwise, attempt to use a free powerup
      else {
        powerup = this._freePowerupsQueue.shift();
      }

      if (!powerup) break;
      let id: BlockId;

      switch (powerup) {
        case 'rocket':
          id = randomPick('rocketHorizontal', 'rocketVertical');
          break;
        case 'cube': {
          // Only allow cubes of the same color as available basic blocks
          const pool = this.scene.basicBlockSystem
            .getActiveBasicBlockIds()
            .map((id) => id.replace('basic', 'cube') as BlockId);
          id = randomPick(...pool);
          break;
        }
        default:
          id = powerup;
      }

      waitFor(baseDelay + delayPerPowerup * index).then(() => {
        if (!this.scene) return;
        this.scene.basicBlockSystem.turnRandomBlockInto(id);
        this.scene.powerBlockSystem.refreshComboGlows();
        this.scene.basicBlockSystem.updateBlockSymbols();
      });
    }

    const spawnCount = Math.min(activeBasicBlocksCount, powerupsQueueCount);

    // Enable back input after all animations
    await waitFor(baseDelay + delayPerPowerup * (spawnCount + 0.5)).then(() => {
      if (!this.scene) return;
      this.scene.phaseSystem.setInputDisable(false);
      this.scene.isApplyingExtraPowerups = false;
    });
  }

  // private: actions
  //-------------------------------------------------------------------------
  private _updateComboGlows() {
    // update combo glows
    this.queries.block.forEach((entity: PowerBlockEntity) =>
      this._updateBlockGlow(entity),
    );
  }

  private _updateBlockGlow(entity: PowerBlockEntity) {
    const glow = entity.c.glow;

    // start else stop glow if has power neighbor
    this._hasPowerNeighbor(entity) ? glow.start() : glow.stop();
  }

  // private: support
  //-------------------------------------------------------------------------
  private _hasPowerNeighbor(entity: PowerBlockEntity): boolean {
    let found = false;

    // iterate direct neighbor base blocks starting at given entity
    blockIterateNeighbors(this.scene.sessionEntity.c.map, entity, (base) => {
      // if not frozen or binded
      if (
        !blockIsFrozen(this.scene, base) &&
        !blockIsBinded(this.scene, base)
      ) {
        // skip ourselves
        if (base === entity) return true;
        // found if power block
        if (powerBlockTypes.includes(base.c.block.props.type as PowerBlockType))
          found = true;
      }
      // dont go past direct neighbor
      return false;
    });

    return found;
  }

  private async consumePowerUp(type: Match2PowerupType, now: number) {
    const frenzyActive = isMatch2TimedRewardActive(
      user(),
      `powerups-${type}`,
      now,
    );

    // Skip if frenzy is currently active
    if (frenzyActive) return;

    // Consume the powerup
    await StateObserver.invoke.consumeMatch2Powerup({ type });
  }
}
